import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import './Profile.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface DietaryOption {
  id: string;
  label: string;
  value: string;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, user: authUser } = useAuth();

  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sendingVerification, setSendingVerification] = useState(false);
  const [dietaryOptions, setDietaryOptions] = useState<DietaryOption[]>([]);
  
  // Address search state
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    dietaryRestrictions: [] as string[],
    preferredAddress: '',
  });

  useEffect(() => {
    fetchProfile();
    fetchDietaryOptions();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/profile`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      setUser(response.data);
      setFormData({
        name: response.data.name || '',
        phone: response.data.phone || '',
        dietaryRestrictions: Array.isArray(response.data.dietaryRestrictions) 
          ? response.data.dietaryRestrictions 
          : [],
        preferredAddress: response.data.preferredAddress || '',
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke hente profil');
    } finally {
      setLoading(false);
    }
  };

  const fetchDietaryOptions = async () => {
    try {
      const response = await axios.get(`${API_URL}/profile/dietary-options`);
      setDietaryOptions(response.data.options);
    } catch (err: any) {
      console.error('Error fetching dietary options:', err);
      // Fallback to default options
      setDietaryOptions([
        { id: 'vegetarian', label: 'Vegetar', value: 'vegetarian' },
        { id: 'vegan', label: 'Vegan', value: 'vegan' },
        { id: 'gluten', label: 'Glutenfri', value: 'gluten' },
        { id: 'lactose', label: 'Laktosefri', value: 'lactose' },
        { id: 'nuts', label: 'Nøtteallergi', value: 'nuts' },
        { id: 'shellfish', label: 'Skalldyrallergi', value: 'shellfish' },
        { id: 'fish', label: 'Fiskallergi', value: 'fish' },
        { id: 'eggs', label: 'Eggallergi', value: 'eggs' },
        { id: 'other', label: 'Annet', value: 'other' },
      ]);
    }
  };

  const searchAddress = async (query: string) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    setIsSearchingAddress(true);
    try {
      const response = await fetch(
        `https://ws.geonorge.no/adresser/v1/sok?sok=${encodeURIComponent(query)}&treffPerSide=10`
      );
      const data = await response.json();
      
      if (data.adresser && data.adresser.length > 0) {
        const suggestions = data.adresser.map((addr: any) => ({
          address: addr.adressetekst,
          municipality: addr.poststed || addr.kommunenavn,
          position: null
        }));
        setAddressSuggestions(suggestions);
        setShowAddressSuggestions(true);
      } else {
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
      }
    } catch (err) {
      console.error('Address search error:', err);
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    } finally {
      setIsSearchingAddress(false);
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData({
      ...formData,
      preferredAddress: value,
    });
    searchAddress(value);
  };

  const selectAddress = (suggestion: any) => {
    const fullAddress = suggestion.municipality 
      ? `${suggestion.address}, ${suggestion.municipality}`
      : suggestion.address;
    setFormData({
      ...formData,
      preferredAddress: fullAddress,
    });
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleDietaryChange = (value: string) => {
    setFormData(prev => {
      const restrictions = prev.dietaryRestrictions;
      if (restrictions.includes(value)) {
        return {
          ...prev,
          dietaryRestrictions: restrictions.filter(r => r !== value),
        };
      } else {
        return {
          ...prev,
          dietaryRestrictions: [...restrictions, value],
        };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      setSaving(true);
      const response = await axios.put(
        `${API_URL}/profile`,
        formData,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      setUser(response.data.user);
      setSuccess('Profil oppdatert!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke oppdatere profil');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestVerification = async () => {
    setError('');
    setSuccess('');

    try {
      setSendingVerification(true);
      await axios.post(
        `${API_URL}/profile/verify-email/request`,
        {},
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      setSuccess('Verifiseringsepost sendt! Sjekk innboksen din.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke sende verifiseringsepost');
    } finally {
      setSendingVerification(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="loading">Laster profil...</div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>Min profil</h1>
        {!user?.emailVerified && (
          <div className="verification-notice">
            <span className="warning-icon">⚠️</span>
            <div>
              <strong>E-postadressen din er ikke verifisert</strong>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                Du vil ikke motta e-postvarsler om arrangementer før du verifiserer e-postadressen din. 
                Du kan fortsatt se all informasjon i LOGG-fanen for dine arrangementer.
              </p>
            </div>
            <button
              onClick={handleRequestVerification}
              disabled={sendingVerification}
              className="btn btn-primary verify-button"
            >
              {sendingVerification ? 'Sender...' : 'Send verifiseringsepost'}
            </button>
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <form onSubmit={handleSubmit} className="profile-form">
        <div className="form-section">
          <h2>Kontoinformasjon</h2>
          
          <div className="form-group">
            <label>E-postadresse</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="disabled-input"
                style={{ flex: 1 }}
              />
              {user?.emailVerified && (
                <span className="email-verified-badge" title="E-post verifisert">
                  ✓ Verifisert
                </span>
              )}
            </div>
            <p className="form-help">E-postadressen kan ikke endres</p>
          </div>

          <div className="form-group">
            <label>Rolle</label>
            <input
              type="text"
              value={user?.role === 'participant' ? 'Deltaker' : user?.role === 'organizer' ? 'Arrangør' : user?.role}
              disabled
              className="disabled-input"
            />
            {user?.role === 'participant' && (
              <button
                type="button"
                onClick={() => navigate('/bli-arrangor')}
                className="btn btn-secondary"
                style={{ marginTop: 'var(--spacing-sm)' }}
              >
                Bli arrangør
              </button>
            )}
          </div>
        </div>

        <div className="form-section">
          <h2>Personlig informasjon</h2>

          <div className="form-group">
            <label htmlFor="name">Navn *</label>
            <input
              id="name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Telefonnummer</label>
            <input
              id="phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+47"
            />
          </div>

          <div className="form-group">
            <label htmlFor="preferredAddress">Foretrukket vertsadresse</label>
            <div style={{ position: 'relative' }}>
              <input
                id="preferredAddress"
                type="text"
                name="preferredAddress"
                value={formData.preferredAddress}
                onChange={handleAddressChange}
                onFocus={() => formData.preferredAddress.length >= 3 && addressSuggestions.length > 0 && setShowAddressSuggestions(true)}
                placeholder="f.eks. Storgata 1, Oslo"
              />
              {isSearchingAddress && (
                <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                  <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>Søker...</span>
                </div>
              )}
              {showAddressSuggestions && addressSuggestions.length > 0 && (
                <div className="address-suggestions" style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  marginTop: '4px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000
                }}>
                  {addressSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => selectAddress(suggestion)}
                      style={{
                        padding: '12px',
                        cursor: 'pointer',
                        borderBottom: index < addressSuggestions.length - 1 ? '1px solid #e5e7eb' : 'none',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <div style={{ fontWeight: '500', color: '#111827' }}>{suggestion.address}</div>
                      {suggestion.municipality && (
                        <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '2px' }}>{suggestion.municipality}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="form-help">
              Denne adressen vil automatisk bli foreslått når du melder deg på et arrangement. Du kan alltid endre den når du melder deg på.
            </p>
          </div>

          <div className="form-group">
            <label>Kosthold / Allergier</label>
            <p className="form-help" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
              Velg alle som gjelder. Dette hjelper vertene med å tilpasse mat som passer for deg (ingen garanti).
            </p>
            <div className="dietary-options">
              {dietaryOptions.map((option) => (
                <label key={option.id} className="dietary-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.dietaryRestrictions.includes(option.value)}
                    onChange={() => handleDietaryChange(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/deltaker')} className="btn btn-secondary cancel-button">
            Avbryt
          </button>
          <button type="submit" disabled={saving} className="btn btn-primary save-button">
            {saving ? 'Lagrer...' : 'Lagre endringer'}
          </button>
        </div>
      </form>

      <div className="profile-info">
        <p className="info-text">
          <strong>Merk:</strong> Endringer i navn, telefonnummer og kosthold vil oppdateres for alle arrangementer du er påmeldt.
        </p>
        <p className="account-created">
          Konto opprettet: {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('nb-NO', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }) : 'Ukjent'}
        </p>
        {user?.loginStats?.totalLogins !== undefined && (
          <p className="account-created">
            Antall pålogginger: {user.loginStats.totalLogins}
          </p>
        )}
      </div>
    </div>
  );
};

export default Profile;
